import React, { useEffect, useMemo, useState } from "react";

import { useTranslation } from "utils";

import { MAX_NAME_LENGTH } from "consts";

import { Button, Flex, Form, Icon, Input, Option, Select, Typography } from "components";

import { getNameValidationError } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";

export interface AddScenarioModalProps {
  isVisible: boolean;
  onClose: () => void;
  onAddScenario: (title: string, copyFromScenarioId?: string) => void;
  existingScenarios?: Array<{
    id: string;
    title: string;
    isReference?: boolean;
  }>;
  isReference?: boolean;
  className?: string;
  dataTestId?: string;
}

type ModalPhase = "choose" | "copy" | "fresh";

type AddScenarioModalFormValues = {
  templateScenarioId?: string;
  newScenarioName?: string;
  scenarioTitle?: string;
};

export const AddScenarioModal = ({ isVisible, onClose, onAddScenario, existingScenarios = [], className = "", dataTestId = "" }: AddScenarioModalProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const [phase, setPhase] = useState<ModalPhase>("choose");
  const [form] = Form.useForm<AddScenarioModalFormValues>();

  const templateLabel = (s: { id: string; title: string; isReference?: boolean }) => (s.isReference ? t("scenarioBuilder.reference") : s.title);

  const normalizedExistingTitles = useMemo(() => new Set(existingScenarios.map((scenario) => scenario.title.trim().toLowerCase())), [existingScenarios]);

  const getValidationError = (value: string): string | null => getNameValidationError(value, t, normalizedExistingTitles);

  const newScenarioNameRules = [
    {
      validator: async (_: unknown, value: string | undefined) => {
        const v = value ?? "";
        if (v === "") {
          return Promise.reject(new Error(t("scenarioBuilder.addScenarioModal.newScenarioNameRequired")));
        }
        const err = getValidationError(v);
        if (err) {
          return Promise.reject(new Error(err));
        }
        return Promise.resolve();
      },
    },
  ];

  const scenarioTitleRules = [
    {
      validator: async (_: unknown, value: string | undefined) => {
        const v = value ?? "";
        if (v === "") {
          return Promise.reject(new Error(t("scenarioBuilder.addScenarioModal.scenarioTitleRequired")));
        }
        const err = getValidationError(v);
        if (err) {
          return Promise.reject(new Error(err));
        }
        return Promise.resolve();
      },
    },
  ];

  const templateScenarioRules = [
    {
      required: true,
      message: t("scenarioBuilder.addScenarioModal.templateRequired"),
    },
  ];

  useEffect(() => {
    if (!isVisible) return;
    setPhase("choose");
    form.resetFields();
  }, [isVisible, form]);

  useEffect(() => {
    if (phase !== "copy" || existingScenarios.length === 0) return;
    form.setFieldsValue({
      templateScenarioId: existingScenarios[0].id,
      newScenarioName: "",
    });
  }, [phase, existingScenarios, form]);

  const templateScenarioId = Form.useWatch("templateScenarioId", form);
  const newScenarioName = Form.useWatch("newScenarioName", form);
  const scenarioTitle = Form.useWatch("scenarioTitle", form);

  const copyNameError = newScenarioName !== undefined && newScenarioName !== null ? getValidationError(String(newScenarioName)) : null;
  const freshTitleError = scenarioTitle !== undefined && scenarioTitle !== null ? getValidationError(String(scenarioTitle)) : null;

  const canCopy = phase === "copy" && Boolean(templateScenarioId) && Boolean(String(newScenarioName ?? "").trim()) && !copyNameError;

  const canAddFresh = phase === "fresh" && Boolean(String(scenarioTitle ?? "").trim()) && !freshTitleError;

  const handleCopySubmit = async () => {
    try {
      const values = await form.validateFields(["templateScenarioId", "newScenarioName"]);
      const title = values.newScenarioName!.trim();
      onAddScenario(title, values.templateScenarioId!);
      onClose();
    } catch {
      /* validation failed */
    }
  };

  const handleAddFresh = async () => {
    try {
      const values = await form.validateFields(["scenarioTitle"]);
      onAddScenario(values.scenarioTitle!.trim(), undefined);
      onClose();
    } catch {
      /* validation failed */
    }
  };

  const handleBackToChoose = () => {
    form.resetFields();
    setPhase("choose");
  };

  const handleOverlayClick = () => {
    onClose();
  };

  const goToCopyPhase = () => {
    setPhase("copy");
  };

  const goToFreshPhase = () => {
    form.setFieldsValue({ scenarioTitle: "" });
    setPhase("fresh");
  };

  if (!isVisible) return null;

  return (
    <div className={`scenario-analysis-modal-overlay ${className}`} data-testid={dataTestId} onClick={handleOverlayClick}>
      <div className="scenario-analysis-modal themed" data-testid={`${dataTestId}-card`} onClick={(e) => e.stopPropagation()}>
        <div className="scenario-analysis-modal-header">
          <div className="scenario-analysis-modal-header-text">
            {phase === "choose" && (
              <>
                <Typography variant="h5" className="scenario-analysis-modal-title">
                  {t("scenarioBuilder.addScenarioModal.newScenario")}
                </Typography>
                <Typography variant="paragraph-s" color="grey-600" className="scenario-analysis-modal-subtitle">
                  {t("scenarioBuilder.addScenarioModal.chooseHowToStart")}
                </Typography>
              </>
            )}
            {phase === "copy" && (
              <>
                <Typography variant="h5" className="scenario-analysis-modal-title">
                  {t("scenarioBuilder.addScenarioModal.copyScenario")}
                </Typography>
                <Typography variant="paragraph-s" color="grey-600" className="scenario-analysis-modal-subtitle">
                  {t("scenarioBuilder.addScenarioModal.useExistingScenarioAsTemplate")}
                </Typography>
              </>
            )}
            {phase === "fresh" && (
              <>
                <Typography variant="h5" className="scenario-analysis-modal-title">
                  {t("scenarioBuilder.addScenarioModal.newScenario")}
                </Typography>
                <Typography variant="paragraph-s" color="grey-600" className="scenario-analysis-modal-subtitle">
                  {t("scenarioBuilder.addScenarioModal.startWithEmptyRotation")}
                </Typography>
              </>
            )}
          </div>
          <Button
            className="scenario-analysis-modal-close"
            onClick={onClose}
            dataTestId={`${dataTestId}-close`}
            aria-label={t("scenarioBuilder.addScenarioModal.close")}
          >
            <Icon iconName="tabler/x" size="m" />
          </Button>
        </div>

        {phase === "choose" && (
          <div className="scenario-analysis-modal-content choose">
            <Button
              className="scenario-analysis-modal-option-card"
              onClick={goToCopyPhase}
              disabled={existingScenarios.length === 0}
              dataTestId={`${dataTestId}-option-copy`}
            >
              <Icon iconName="tabler/copy" size="m" className="scenario-analysis-modal-option-card-icon" />
              <span className="scenario-analysis-modal-option-card-title">{t("scenarioBuilder.copyFrom")}</span>
              <span className="scenario-analysis-modal-option-card-desc">{t("scenarioBuilder.addScenarioModal.useExistingScenarioAsTemplate")}</span>
            </Button>
            <Button className="scenario-analysis-modal-option-card" onClick={goToFreshPhase} dataTestId={`${dataTestId}-option-fresh`}>
              <Icon iconName="klim/plus" size="m" className="scenario-analysis-modal-option-card-icon" />
              <span className="scenario-analysis-modal-option-card-title">{t("scenarioBuilder.createNew")}</span>
              <span className="scenario-analysis-modal-option-card-desc">{t("scenarioBuilder.addScenarioModal.beginWithEmptyRotation")}</span>
            </Button>
          </div>
        )}

        {(phase === "copy" || phase === "fresh") && (
          <Form form={form} validateTrigger={["onChange", "onBlur"]} className="scenario-analysis-modal-form">
            {phase === "copy" && (
              <div className="scenario-analysis-modal-content">
                <div className="scenario-analysis-modal-form-group">
                  <div className="scenario-analysis-modal-input-group">
                    <Select
                      name="templateScenarioId"
                      value={templateScenarioId ?? undefined}
                      label={t("scenarioBuilder.addScenarioModal.template")}
                      rules={templateScenarioRules}
                      dataTestId={`${dataTestId}-template`}
                      allowClear={false}
                      className="scenario-analysis-modal-template-select"
                    >
                      {existingScenarios.map((option) => (
                        <Option key={option.id} value={option.id}>
                          {templateLabel(option)}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div className="scenario-analysis-modal-input-group">
                    <Input
                      name="newScenarioName"
                      value={newScenarioName ?? undefined}
                      dataTestId={`${dataTestId}-copy-name`}
                      label={t("scenarioBuilder.addScenarioModal.newScenarioName")}
                      placeholder={t("scenarioBuilder.addScenarioModal.scenarioNamePlaceholder")}
                      className="scenario-analysis-modal-title-input"
                      maxLength={MAX_NAME_LENGTH + 1}
                      rules={newScenarioNameRules}
                      onPressEnter={handleCopySubmit}
                    />
                  </div>
                </div>
              </div>
            )}

            {phase === "fresh" && (
              <div className="scenario-analysis-modal-content">
                <Input
                  name="scenarioTitle"
                  dataTestId={`${dataTestId}-title`}
                  label={t("scenarioBuilder.scenarioTitle")}
                  placeholder={t("scenarioBuilder.addScenarioModal.enterScenarioTitle")}
                  className="scenario-analysis-modal-title-input"
                  maxLength={MAX_NAME_LENGTH + 1}
                  rules={scenarioTitleRules}
                  onPressEnter={handleAddFresh}
                />
              </div>
            )}
          </Form>
        )}

        {phase !== "choose" && (
          <div className="scenario-analysis-modal-footer">
            <Flex className="scenario-analysis-modal-footer-actions" justify="space-between" align="center" gap="1rem" style={{ width: "100%" }}>
              <Button type="text" onClick={handleBackToChoose} dataTestId={`${dataTestId}-back`}>
                {t("scenarioBuilder.addScenarioModal.back")}
              </Button>
              {phase === "copy" ? (
                <Button
                  type="primary"
                  className="button-no-min-width auto-width"
                  onClick={handleCopySubmit}
                  disabled={!canCopy}
                  style={{ maxWidth: "fit-content" }}
                  dataTestId={`${dataTestId}-copy-confirm`}
                >
                  {t("scenarioBuilder.copy")}
                </Button>
              ) : (
                <Button
                  type="primary"
                  className="button-no-min-width auto-width"
                  onClick={handleAddFresh}
                  disabled={!canAddFresh}
                  style={{ maxWidth: "fit-content" }}
                  dataTestId={`${dataTestId}-confirm`}
                >
                  {t("scenarioBuilder.addScenario")}
                </Button>
              )}
            </Flex>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddScenarioModal;
